import { JIKE_WEB_HOST } from '@/utils/request/host'
import { PostRouteType } from '@/utils/route'
import { AxiosError, isAxiosError } from 'axios'
import { z } from 'zod'

const POST_ID_REGEXP = /^[0-9a-fA-F]{24}$/

export const PUBLIC_POST_ERROR_MESSAGES = {
	invalidId: '动态 ID 格式无效',
	unavailable: '动态不存在或不可公开使用',
	cancelled: '动态查询已取消',
	rateLimited: '动态查询过于频繁，请稍后重试',
	network: '动态查询失败，请稍后重试',
} as const

const RAW_GET_POST_INPUT_SCHEMA = z.strictObject({
	id: z.string().regex(POST_ID_REGEXP),
})

export const GET_POST_INPUT_SCHEMA = RAW_GET_POST_INPUT_SCHEMA.transform(({ id }) => ({
	id: id.toLowerCase(),
}))

export const GET_POST_INPUT_JSON_SCHEMA = z.toJSONSchema(RAW_GET_POST_INPUT_SCHEMA)

export const PUBLIC_POST_SOURCE_FIELDS_SCHEMA = z.object({
	id: z.string().regex(POST_ID_REGEXP),
	type: z.literal('ORIGINAL_POST'),
	content: z.string(),
	createdAt: z.string(),
	user: z.object({
		username: z.string(),
		screenName: z.string(),
		avatarImage: z.object({
			picUrl: z.url(),
			thumbnailUrl: z.url().optional(),
		}),
	}),
	topic: z
		.object({
			id: z.string(),
			content: z.string(),
		})
		.optional(),
	poi: z
		.object({
			name: z.string(),
		})
		.optional(),
})

const PUBLIC_POST_SNAPSHOT_SCHEMA = PUBLIC_POST_SOURCE_FIELDS_SCHEMA.extend({
	link: z.url(),
})

const buildPublicPostLink = (username: string, id: string) =>
	`${JIKE_WEB_HOST}/u/${encodeURIComponent(username)}/${PostRouteType.POST}/${id}`

export const PUBLIC_POST_VISIBILITY_SCHEMA = z.object({
	status: z.literal('NORMAL'),
	deleted: z.literal(false).optional(),
	isPrivate: z.literal(false).optional(),
	isSuppressed: z.literal(false).optional(),
})

const PUBLIC_ORIGINAL_POST_SOURCE_SCHEMA = PUBLIC_POST_SOURCE_FIELDS_SCHEMA.extend(
	PUBLIC_POST_VISIBILITY_SCHEMA.shape
)
	.transform(
		({
			status: _status,
			deleted: _deleted,
			isPrivate: _isPrivate,
			isSuppressed: _isSuppressed,
			...snapshot
		}) => ({
			...snapshot,
			link: buildPublicPostLink(snapshot.user.username, snapshot.id),
		})
	)
	.pipe(PUBLIC_POST_SNAPSHOT_SCHEMA)

export type PublicPostSnapshot = z.infer<typeof PUBLIC_POST_SNAPSHOT_SCHEMA>

class PublicPostContractError extends Error {}

export function parsePublicPostSnapshot(data: unknown, expectedId: string): PublicPostSnapshot {
	const snapshot = PUBLIC_ORIGINAL_POST_SOURCE_SCHEMA.parse(data)

	if (snapshot.id !== expectedId) {
		throw new PublicPostContractError()
	}

	return snapshot
}

export function normalizePublicPostError(error: unknown): Error {
	const responseStatus = isAxiosError(error) ? error.response?.status : undefined

	if (
		(isAxiosError(error) && error.code === AxiosError.ERR_CANCELED) ||
		(typeof DOMException !== 'undefined' &&
			error instanceof DOMException &&
			error.name === 'AbortError')
	) {
		return new Error(PUBLIC_POST_ERROR_MESSAGES.cancelled)
	}

	if (responseStatus === 429) {
		return new Error(PUBLIC_POST_ERROR_MESSAGES.rateLimited)
	}

	if (
		error instanceof PublicPostContractError ||
		error instanceof z.ZodError ||
		(responseStatus !== undefined && responseStatus >= 400 && responseStatus < 500)
	) {
		return new Error(PUBLIC_POST_ERROR_MESSAGES.unavailable)
	}

	return new Error(PUBLIC_POST_ERROR_MESSAGES.network)
}
